import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Paper,
  Alert,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowBack as ArrowBackIcon,
  PlayArrow as PlayArrowIcon,
  AccountBalance as AccountBalanceIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { fetchTemplates } from '../redux/slices/templateSlice';
import { fetchTemplateCategories } from '../redux/slices/templateCategorySlice';
import { useHasPermission } from '../hooks/usePermission';
import ForbiddenPage from './ForbiddenPage';

/**
 * DocumentTemplateSelectionPage - Template selection for document creation
 * 
 * Features:
 * - Template list (only for document creation, not management)
 * - Search and category filters
 * - Template cards with "Belge Oluştur" button
 * - Permission: documents.create (NOT templates.view)
 * 
 * Phase 6C: Document Management
 */
const DocumentTemplateSelectionPage = () => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { templates, loading, pagination, error } = useSelector(
    (state) => state.templates
  );
  const categories = useSelector((state) => state.templateCategories.list);

  // Her mount'ta kategorileri fetch et (güncel liste için)
  useEffect(() => {
    dispatch(fetchTemplateCategories());
  }, [dispatch]);

  // Permission: documents.create (NOT templates.view)
  const hasPermission = useHasPermission('documents.create');

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // Debounce search term (500ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch templates on mount & filter/page/limit change
  useEffect(() => {
    if (hasPermission) {
      const params = {
        search: debouncedSearch,
        category: categoryFilter,
        page,
        limit,
      };

      Object.keys(params).forEach((key) => {
        if (params[key] === '') {
          delete params[key];
        }
      });

      dispatch(fetchTemplates(params));
    }
  }, [dispatch, debouncedSearch, categoryFilter, page, limit, hasPermission]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newLimit) => {
    setLimit(newLimit >= 20 ? newLimit : 20);
    setPage(1);
  };

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle category filter
  const handleCategoryFilter = (e) => {
    setCategoryFilter(e.target.value);
  };

  // Handle back
  const handleBack = () => {
    history.goBack();
  };

  // Permission-based guard check
  if (!hasPermission) {
    return <ForbiddenPage />;
  }

  return (
    <Container maxWidth="xl">
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={handleBack}
        sx={{ mb: 3 }}
      >
        Geri Dön
      </Button>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">
            Şablon Seçin
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Yeni doküman oluşturmak için bir şablon seçin
          </Typography>
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters (in Paper) */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {/* Search */}
          <TextField
            placeholder="Şablon adı veya açıklama ile ara..."
            value={searchTerm}
            onChange={handleSearch}
            sx={{ flex: 1, minWidth: 300 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />

          {/* Category Filter */}
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Kategori</InputLabel>
            <Select
              value={categoryFilter}
              label="Kategori"
              onChange={handleCategoryFilter}
            >
              <MenuItem value="">Tümü</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat._id} value={cat.name}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Template Table */}
      {loading && templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Şablonlar yükleniyor...
          </Typography>
        </Paper>
      ) : !loading && templates.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Şablon bulunamadı
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Arama kriterlerinizi değiştirerek tekrar deneyin
          </Typography>
        </Paper>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Şablon Adı</TableCell>
                  <TableCell>Kategori</TableCell>
                  <TableCell>Soru Sayısı</TableCell>
                  <TableCell>Kredi Ücreti</TableCell>
                  <TableCell>Oluşturan</TableCell>
                  <TableCell>Oluşturulma Tarihi</TableCell>
                  <TableCell align="right">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template._id} hover>
                    {/* Template Title */}
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {template.title}
                        </Typography>
                        {template.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {template.description.length > 100
                              ? `${template.description.substring(0, 100)}...`
                              : template.description}
                          </Typography>
                        )}
        </Box>
                    </TableCell>

                    {/* Category */}
                    <TableCell>
                  <Chip
                    label={template.category || 'Diğer'}
                    size="small"
                    color="primary"
                  />
                    </TableCell>

                    {/* Question Count */}
                    <TableCell>
                    <Chip
                        label={`${template.questions?.length || 0} Soru`}
                      size="small"
                      variant="outlined"
                    />
                    </TableCell>

                    {/* Credit Cost */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccountBalanceIcon fontSize="small" color="primary" />
                        <Typography variant="body2" fontWeight="medium">
                          {template.creditCost !== null && template.creditCost !== undefined 
                            ? `${parseFloat(template.creditCost).toFixed(1)} Kredi`
                            : '1 Kredi'}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Created By */}
                    <TableCell>
                      {template.createdBy ? (
                        <Typography variant="body2">
                      {template.createdBy.firstName} {template.createdBy.lastName}
                    </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    {/* Created Date */}
                    <TableCell>
                      {template.createdAt ? (
                        <Box>
                          <Typography variant="body2">
                            {format(new Date(template.createdAt), 'dd MMM yyyy', { locale: tr })}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {format(new Date(template.createdAt), 'HH:mm')}
                          </Typography>
                </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right">
              <Button
                variant="contained"
                        size="small"
                startIcon={<PlayArrowIcon />}
                        onClick={() => history.push(`/documents/new/${template._id}`)}
              >
                Belge Oluştur
              </Button>
                    </TableCell>
                  </TableRow>
          ))}
              </TableBody>
            </Table>
          </TableContainer>

      {/* Pagination */}
          {pagination.total > 0 && (() => {
            const validRowsPerPageOptions = [10, 20, 50, 100];
            // Ensure minimum limit of 10
            const currentLimit = pagination.limit && pagination.limit >= 20
              ? pagination.limit 
              : 20; // Default to 20 if limit is less than 20or undefined
            // Normalize rowsPerPage to a valid option (must be in rowsPerPageOptions)
            const normalizedRowsPerPage = validRowsPerPageOptions.includes(currentLimit)
              ? currentLimit
              : validRowsPerPageOptions[0]; // Default to first option (10) if invalid
            
            return (
              <TablePagination
                component="div"
                count={pagination.total || 0}
                page={(pagination.page || 1) - 1}
                onPageChange={(event, newPage) => handlePageChange(newPage + 1)}
                rowsPerPage={normalizedRowsPerPage}
                onRowsPerPageChange={(event) => handleRowsPerPageChange(parseInt(event.target.value, 10))}
                rowsPerPageOptions={validRowsPerPageOptions}
                labelRowsPerPage="Sayfa başına:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              />
            );
          })()}
        </Paper>
      )}
    </Container>
  );
};

export default DocumentTemplateSelectionPage;

